import * as React from 'react'
import { Info, PackageX, Trash2 } from 'lucide-react'
import {
  useFieldArray,
  useFormContext,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useLocations, usePOBudget } from '@/hooks/usePOs'
import { usePrefillProducts } from '@/hooks/usePrefillProducts'
import { useVendors } from '@/hooks/useVendors'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { apiClient } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/lib/dates'
import { BUDGET_FILL, budgetLevel, projectedSpend } from '@/lib/budget'
import type { POFormValues, POLineItemFormValues } from '@/lib/schemas/po'
import { ProductSearchPicker, productToLineItem } from './ProductSearchPicker'
import { vendorsForSupplier } from './SupplierCombobox'
import { RecipientCombobox } from './RecipientCombobox'
import { LocationCombobox } from './LocationCombobox'
import { ItemDetailsModal } from './ItemDetailsModal'
import { signalKind } from '@/hooks/useDashboard'

export type WizardStep = 1 | 2 | 3

interface POWizardProps {
  step: WizardStep
  disabled?: boolean
  // Optional handler — when supplied, the left-rail step entries become
  // clickable buttons that jump directly to the chosen step. Read-only
  // surfaces (e.g. a non-DRAFT View) pass it through; surfaces that want to
  // gate navigation behind validation (CreatePOModal's "you must finish
  // step 1 before reaching step 2") can omit it.
  onStepClick?: (n: WizardStep) => void
  // When the wizard is showing an AWAITING_APPROVAL PO, the Review step
  // renders an inline Approve / Reject panel (Reject opens a reason
  // textarea) instead of sending the approver to a separate modal.
  poStatus?: string
  // The PO's stored approval_notes thread (server-managed, append-only).
  // Shown read-only on the Review step above the fresh add-a-note input.
  // Absent for new POs (CreatePOModal).
  approvalNotesThread?: string | null
  onApprove?: () => void | Promise<void>
  onReject?: (reason: string) => void | Promise<void>
  approveBusy?: boolean
  rejectBusy?: boolean
  // When supplied (existing/saved POs only — EditPOModal), the Review step
  // renders a "Post note" button next to the add-a-note textarea that posts
  // the note immediately as a standalone conversation entry (any status, no
  // status change). CreatePOModal omits it — there's no saved PO to note yet.
  // The parent clears the textarea on success. The note controls are exempt
  // from `disabled` so a user can comment even on an otherwise read-only PO
  // (e.g. ORDERED / ARCHIVED).
  onPostNote?: (text: string) => void | Promise<void>
  postNoteBusy?: boolean
  // When supplied (placed POs only), each Items-step row gets a "Backorder"
  // action that removes the item from the PO and moves its unreceived units to
  // the backorder / OOS list. Receives the row's variant_id.
  onBackorderItem?: (variantId: number) => void
}

// ────────────────────────────────────────────────────────────────────
// Currency / number helpers (one definition; the three step components
// each read it locally)
// ────────────────────────────────────────────────────────────────────
export function fmtCurrency(n: number, dp = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(n)
}

export function fmtCurrency0(n: number): string {
  return fmtCurrency(n, 0)
}

// Budget bar + status label, shared by the Items toolbar and Review step.
// `spent` is whatever figure the caller already shows; the bar caps at full
// width but the label names any overspend. No bar for a zero/negative budget.
export function BudgetMeter({
  spent,
  budget,
  className,
}: {
  spent: number
  budget: number
  className?: string
}) {
  if (!(budget > 0)) return null
  const ratio = spent / budget
  const level = budgetLevel(ratio)
  return (
    <>
      <div
        data-testid="budget-meter"
        className={cn(
          'relative h-1.5 overflow-hidden border border-[var(--line-warm)] bg-[var(--meter-track)]',
          className
        )}
      >
        <div
          data-testid="budget-meter-fill"
          className={cn('absolute inset-y-0 left-0', BUDGET_FILL[level])}
          style={{ width: `${Math.max(0, Math.min(ratio, 1)) * 100}%` }}
        />
      </div>
      {level === 'near' && (
        <span className="text-xs text-[var(--status-awaiting-fg)]">Near budget</span>
      )}
      {level === 'over' && (
        <span className="text-xs font-medium text-[var(--status-rejected-fg)]">
          Over budget by {fmtCurrency0(spent - budget)}
        </span>
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────────────
// Derived totals used in Items + Review + footer
// ────────────────────────────────────────────────────────────────────
export interface POTotals {
  subtotal: number
  freight: number
  duty: number
  total: number
  freightPct: number
  dutyPct: number
  units: number
  itemCount: number
  mov: number
  movMet: boolean
}

export function usePOTotals(): POTotals {
  const { control } = useFormContext<POFormValues>()
  const supplier = useWatch({ control, name: 'supplier' })
  const vendor = useWatch({ control, name: 'vendor' })
  const items = useWatch({ control, name: 'items' }) ?? []
  const { data: vendors = [] } = useVendors()

  // Session 7.5 — freight/duty/MOV are stored per-vendor in vendor_rules.
  // - Vendor-only PO: use that vendor's rates directly (the common case).
  // - Supplier PO: suppliers usually share one rate across the brands they
  //   carry, so we use the first vendor under this supplier as the canonical
  //   source. If brands diverge, vendor_rules needs a supplier-level
  //   override table (out of scope here).
  const v = supplier
    ? vendors.find((x) => (x.supplier ?? '').trim() === supplier)
    : vendor
      ? vendors.find((x) => x.vendor === vendor)
      : undefined
  const freightPct = v?.freight_pct ?? 0
  const dutyPct = v?.duty_pct ?? 0
  const mov = v?.mov_dollars ?? 0

  const subtotal = items.reduce(
    (s, it) => s + (Number(it.cost_base) || 0) * (Number(it.qty_ordered) || 0),
    0
  )
  const freight = subtotal * freightPct
  const duty = subtotal * dutyPct
  const total = subtotal + freight + duty
  const units = items.reduce((s, it) => s + (Number(it.qty_ordered) || 0), 0)

  return {
    subtotal,
    freight,
    duty,
    total,
    freightPct,
    dutyPct,
    units,
    itemCount: items.length,
    mov,
    movMet: mov === 0 ? true : subtotal >= mov,
  }
}

// ────────────────────────────────────────────────────────────────────
// Left rail: vertical stepper + live vendor context
// ────────────────────────────────────────────────────────────────────
function StepperRail({
  step,
  filledSupplier,
  filledVendor,
  filledLocation,
  onStepClick,
}: {
  step: WizardStep
  filledSupplier: string
  filledVendor: string
  filledLocation: string
  onStepClick?: (n: WizardStep) => void
}) {
  const { data: vendors = [] } = useVendors()
  const carried = vendorsForSupplier(vendors, filledSupplier)
  // Canonical vendor record for rate display: first vendor under the
  // supplier, or the directly-picked vendor.
  const v = filledSupplier
    ? vendors.find((x) => (x.supplier ?? '').trim() === filledSupplier)
    : filledVendor
      ? vendors.find((x) => x.vendor === filledVendor)
      : undefined
  const filled = !!filledSupplier || !!filledVendor

  const { control } = useFormContext<POFormValues>()
  const expected = useWatch({ control, name: 'expected_delivery' })

  const steps: { num: WizardStep; lab: string; sub: string }[] = [
    { num: 1, lab: 'Setup', sub: 'Recipient, location, dates' },
    { num: 2, lab: 'Items', sub: 'Quantities and costs' },
    { num: 3, lab: 'Review', sub: 'Approval & submit' },
  ]

  return (
    <div className="flex w-[220px] flex-none flex-col border-r border-[var(--line-warm)] bg-[var(--surface-page)] p-5">
      <div className="flex flex-col gap-[22px]">
        {steps.map((s) => {
          const state =
            s.num < step ? 'done' : s.num === step ? 'active' : 'pending'
          const clickable = !!onStepClick
          const inner = (
            <>
              <div
                className={cn(
                  'flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border text-[11px] font-bold',
                  state === 'done' &&
                    'border-[var(--brand)] bg-[var(--brand)] text-white',
                  state === 'active' && 'border-[var(--brand)] text-[var(--brand)]',
                  state === 'pending' &&
                    'border-[var(--line-warm)] text-[var(--mute)]'
                )}
              >
                {state === 'done' ? '✓' : s.num}
              </div>
              <div>
                <div
                  className={cn(
                    'text-sm font-semibold',
                    state === 'pending' && 'text-[var(--mute)]'
                  )}
                >
                  {s.lab}
                </div>
                <div className="text-xs text-[var(--mute)]">{s.sub}</div>
              </div>
            </>
          )
          if (clickable) {
            return (
              <button
                key={s.num}
                type="button"
                onClick={() => onStepClick?.(s.num)}
                className={cn(
                  'flex items-start gap-2.5 text-left rounded px-1 -mx-1 py-0.5 cursor-pointer',
                  // Pending entries are dimmed but still clickable so users
                  // can preview future steps before completing the current one.
                  'hover:bg-[var(--surface-header)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand)]',
                  state === 'active' && 'bg-[var(--surface-header)]'
                )}
                aria-current={state === 'active' ? 'step' : undefined}
                aria-label={`Go to step ${s.num}: ${s.lab}`}
              >
                {inner}
              </button>
            )
          }
          return (
            <div key={s.num} className="flex items-start gap-2.5">
              {inner}
            </div>
          )
        })}
      </div>

      <div className="mt-6 border-t border-[var(--line-warm)] pt-3.5">
        {!filled ? (
          <div className="text-xs leading-[1.5] text-[var(--mute)]">
            Pick a supplier or vendor and a location to see lead time, MOV,
            and predicted ETA here.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5 text-[11px]">
            {filledSupplier ? (
              <ContextRow label="Supplier" value={filledSupplier} strong />
            ) : (
              <ContextRow label="Vendor" value={filledVendor} strong />
            )}
            {filledSupplier && carried.length > 0 && (
              <ContextRow
                label={`Vendors carried (${carried.length})`}
                value={carried.join(', ')}
              />
            )}
            {filledLocation && (
              <ContextRow label="Receive at" value={filledLocation} />
            )}
            {v && (
              <ContextRow
                label="Lead time · MOV"
                value={`${v.lead_time} + ${v.buffer_days}d · ${fmtCurrency0(
                  v.mov_dollars
                )}`}
                strong
              />
            )}
            {v?.payment_terms && (
              <ContextRow label="Payment terms" value={v.payment_terms} />
            )}
            {v && (
              <ContextRow
                label="Duty % · Duty payer"
                // duty_pct is a FRACTION (0.05 = 5%) — this rendered it raw as
                // "0.05%", understating duty 100×. The cost summary further
                // down this same file already does `* 100`; this row was the
                // one place that didn't.
                value={`${((v.duty_pct ?? 0) * 100).toFixed(1)}% · ${(v as { duty_payer?: string | null }).duty_payer ?? '—'}`}
              />
            )}
            {expected && (
              <ContextRow label="Expected delivery" value={expected} strong />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ContextRow({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string
  strong?: boolean
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
        {label}
      </div>
      <div className={cn('text-sm', strong ? 'font-semibold' : undefined)}>
        {value}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Step 1: Setup
// ────────────────────────────────────────────────────────────────────
function StepSetup({ disabled }: { disabled: boolean }) {
  const { control, setValue, clearErrors } = useFormContext<POFormValues>()
  const { data: locations = [] } = useLocations()
  const { data: vendors = [] } = useVendors()

  const supplier = useWatch({ control, name: 'supplier' }) ?? ''
  const vendor = useWatch({ control, name: 'vendor' }) ?? ''
  const carried = vendorsForSupplier(vendors, supplier)
  // For payment-terms display: first vendor under the supplier, or the
  // directly-picked vendor.
  const recipientVendorDetails = supplier
    ? vendors.find((v) => (v.supplier ?? '').trim() === supplier)
    : vendor
      ? vendors.find((v) => v.vendor === vendor)
      : undefined

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <StepHeader
        title="Setup"
        sub="Supplier or vendor, receive location and delivery window"
      />

      <div className="flex flex-col gap-4 overflow-y-auto p-6">
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          {/* Recipient picker (supplier OR vendor). The FormField is wired
              to `vendor` so the at-least-one Zod refine surfaces its error
              under this control regardless of which path the user takes. */}
          <FormField
            control={control}
            name="vendor"
            render={() => (
              <FormItem>
                <FormLabel>Supplier or vendor</FormLabel>
                <FormControl>
                  <RecipientCombobox
                    supplier={supplier}
                    vendor={vendor}
                    onChange={(sel) => {
                      if (sel.kind === 'supplier') {
                        setValue('supplier', sel.value, { shouldValidate: true })
                        setValue('vendor', '', { shouldValidate: true })
                      } else {
                        setValue('vendor', sel.value, { shouldValidate: true })
                        setValue('supplier', '', { shouldValidate: true })
                      }
                      clearErrors(['vendor', 'supplier'])
                    }}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="location_to_receive"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Receive at</FormLabel>
                <FormControl>
                  {/* Combobox (same pattern as the vendor picker) instead of a
                      Radix <Select> — it renders `value` directly as the
                      trigger label, so the location shows correctly on reopen
                      regardless of when the locations list / form value load.
                      The Radix Select derived its text from a matching
                      <SelectItem> at set-time and went blank when the item
                      wasn't mounted yet. */}
                  <LocationCombobox
                    value={field.value ?? ''}
                    locations={locations}
                    onChange={(val) =>
                      setValue('location_to_receive', val, {
                        shouldDirty: true,
                        shouldValidate: true,
                        shouldTouch: true,
                      })
                    }
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* "Vendors carried" lives in its own row below the grid so it can't
            push the right column out of alignment when the supplier is set. */}
        {supplier && (
          <div className="text-xs text-[var(--mute)]">
            {carried.length > 0 ? (
              <>
                Vendors carried:{' '}
                <span className="font-medium text-[var(--ink-2)]">
                  {carried.join(', ')}
                </span>
              </>
            ) : (
              <span className="text-[var(--status-rejected-fg)]">
                No vendors are flagged as carried by this supplier
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField
            control={control}
            name="expected_delivery"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected delivery</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    disabled={disabled}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                </FormControl>
                <div className="text-xs text-[var(--mute)]">Optional</div>
              </FormItem>
            )}
          />
          <FormItem>
            <FormLabel>Payment terms</FormLabel>
            <div className="flex h-9 items-center rounded-md border border-input bg-[var(--surface-page)] px-3 text-base text-[var(--ink-2)]">
              {recipientVendorDetails?.payment_terms || '—'}
            </div>
            <div className="text-xs text-[var(--mute)]">
              {supplier ? "From supplier's first vendor profile" : 'From vendor profile'}
            </div>
          </FormItem>
          <FormField
            control={control}
            name="vendor_po_ref"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vendor PO ref</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="optional"
                    disabled={disabled}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                </FormControl>
                <div className="text-xs text-[var(--mute)]">
                  Vendor's own number, if any
                </div>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes for vendor</FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  disabled={disabled}
                  rows={2}
                  placeholder="Anything buyer or the vendor should know about this order…"
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PrefillCard supplier={supplier} vendor={vendor} disabled={disabled} />
      </div>
    </div>
  )
}

function PrefillCard({
  supplier,
  vendor,
  disabled,
}: {
  supplier: string
  vendor: string
  disabled: boolean
}) {
  const { control } = useFormContext<POFormValues>()
  const { append } = useFieldArray({ control, name: 'items' })
  const items = useWatch({ control, name: 'items' }) ?? []
  const { data: candidates = [], isLoading } = usePrefillProducts({
    supplier: supplier || null,
    vendor: supplier ? null : vendor || null,
  })

  // Hide candidates already on the PO
  const existingVariantIds = new Set(items.map((it) => it.variant_id))
  const newOnes = candidates.filter((c) => {
    const vid = Number(c.variant_id ?? 0)
    return vid && !existingVariantIds.has(vid)
  })

  if (!supplier && !vendor) return null

  function addAll() {
    newOnes.forEach((c) => {
      const variantId = Number(c.variant_id ?? 0)
      const productId = Number(c.product_id ?? 0)
      if (!variantId || !productId) return
      append({
        variant_id: variantId,
        product_id: productId,
        product_title: c.product_title ?? '',
        variant_title: c.variant_title ?? '',
        sku: c.sku ?? '',
        barcode: c.barcode ?? '',
        vendor: c.vendor ?? '',
        retail_price: 0,
        cost_base: c.unit_cost ?? 0,
        qty_ordered: c.recommended_qty || 1,
        notes: '',
        on_hand: c.on_hand ?? 0,
        incoming: c.incoming ?? 0,
        rec_qty: c.recommended_qty ?? 0,
        signal: signalKind(c.action_signal),
        return_rate: c.return_rate ?? undefined,
      })
    })
  }

  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-page)] p-3.5">
      <div className="flex items-center gap-2.5">
        <div className="text-base font-semibold">Prefill from dashboard</div>
        <div className="text-xs text-[var(--mute)]">
          {isLoading
            ? 'Checking…'
            : `${newOnes.length} item${newOnes.length === 1 ? '' : 's'} flagged ORDER NOW match this ${supplier ? 'supplier' : 'vendor'}`}
        </div>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || isLoading || newOnes.length === 0}
          onClick={addAll}
          data-action="prefill-all"
        >
          Auto-add now
        </Button>
      </div>
      {newOnes.length > 0 && (
        <div className="mt-2 max-h-24 overflow-y-auto text-[11px] leading-[1.5]">
          {newOnes.slice(0, 6).map((c) => (
            <div key={`${c.variant_id}-${c.barcode}`}>
              · {c.product_title}
              {c.variant_title ? `, ${c.variant_title}` : ''} — qty{' '}
              {c.recommended_qty ?? 1}
            </div>
          ))}
          {newOnes.length > 6 && (
            <div className="text-[var(--mute)]">+ {newOnes.length - 6} more…</div>
          )}
        </div>
      )}
    </div>
  )
}

function StepHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="border-b border-[var(--line-warm)] px-6 py-4">
      <div className="text-base font-semibold">{title}</div>
      <div className="text-xs text-[var(--mute)]">{sub}</div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Step 2: Items
// ────────────────────────────────────────────────────────────────────
function StepItems({
  disabled,
  poStatus,
  onBackorderItem,
}: {
  disabled: boolean
  poStatus?: string
  onBackorderItem?: (variantId: number) => void
}) {
  // Show a Received column only once the PO has been ordered — that's when
  // qty_received is meaningful. Hidden during create/draft/approval so the
  // buying flow stays unchanged.
  const showReceived =
    poStatus === 'ORDERED' ||
    poStatus === 'PARTIAL_ORDERED' ||
    poStatus === 'ARCHIVED'
  const { control, register, formState, getValues, setValue } =
    useFormContext<POFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'items' })
  const totals = usePOTotals()
  const { data: budget } = usePOBudget()
  const supplier = useWatch({ control, name: 'supplier' }) ?? ''
  const vendor = useWatch({ control, name: 'vendor' }) ?? ''
  const [detailsIndex, setDetailsIndex] = React.useState<number | null>(null)
  const [scanError, setScanError] = React.useState<string | null>(null)

  // Lazy single error-beep, mirrors the Receiving page. jsdom's play() is a
  // no-op that returns undefined, so guard the promise shape.
  const errorAudioRef = React.useRef<HTMLAudioElement | null>(null)
  function playScanError() {
    try {
      if (!errorAudioRef.current) {
        errorAudioRef.current = new Audio('/scan-error.mp3')
      }
      const p = errorAudioRef.current.play()
      if (p && typeof p.catch === 'function') p.catch(() => {})
    } catch {
      /* audio unavailable — ignore */
    }
  }

  // Guards against a duplicate row when the same new barcode is scanned twice
  // before its lookup resolves: the in-flight barcode is tracked, and extra
  // scans bump a pending counter that's folded into the qty once the row lands.
  const inFlightRef = React.useRef<Set<string>>(new Set())
  const pendingRef = React.useRef<Map<string, number>>(new Map())

  async function handleScan(raw: string) {
    if (disabled) return
    const barcode = raw.trim()
    if (!barcode) return
    setScanError(null)

    // Already on the PO → bump qty +1 (scan-to-count). Cheap + synchronous.
    const current = getValues('items') ?? []
    const idx = current.findIndex((it) => (it.barcode ?? '') === barcode)
    if (idx >= 0) {
      const next = (Number(current[idx].qty_ordered) || 0) + 1
      setValue(`items.${idx}.qty_ordered`, next, {
        shouldValidate: true,
        shouldDirty: true,
      })
      return
    }

    // A new-item lookup for this barcode is already running — count this scan
    // and let the in-flight resolution add the combined qty.
    if (inFlightRef.current.has(barcode)) {
      pendingRef.current.set(barcode, (pendingRef.current.get(barcode) ?? 0) + 1)
      return
    }
    inFlightRef.current.add(barcode)
    try {
      const supplierClean = (supplier || '').trim() || undefined
      const vendorClean = (vendor || '').trim() || undefined
      const { data, error } = await apiClient.GET('/api/inventory/products', {
        params: {
          query: {
            search: barcode,
            limit: 20,
            ...(supplierClean ? { supplier: supplierClean } : {}),
            ...(vendorClean && !supplierClean ? { vendor: vendorClean } : {}),
          },
        },
      })
      const match = error
        ? undefined
        : (data ?? []).find((p) => (p.barcode ?? '') === barcode)
      if (!match || !Number(match.variant_id) || !Number(match.product_id)) {
        playScanError()
        setScanError(
          `No product found for barcode ${barcode} in this ${
            supplier ? 'supplier' : vendor ? 'vendor' : 'catalog'
          }${supplier || vendor ? "'s catalog" : ''}.`
        )
        return
      }
      const extra = pendingRef.current.get(barcode) ?? 0
      // shouldFocus:false — RHF's append defaults to focusing the new row's
      // first input (the note field), which would steal focus from the search
      // bar and break consecutive scanning. Keep focus where it is.
      append(productToLineItem(match, 1 + extra), { shouldFocus: false })
    } catch {
      playScanError()
      setScanError('Scan lookup failed — try again.')
    } finally {
      inFlightRef.current.delete(barcode)
      pendingRef.current.delete(barcode)
    }
  }

  // Window-level capture so a scan registers even when focus has drifted off
  // the search box (e.g. the user clicked a qty cell). The hook ignores
  // keystrokes while an <input>/<textarea> is focused, so the search box owns
  // its own scans via ProductSearchPicker's onScan.
  useBarcodeScanner(handleScan)

  const itemsError = formState.errors.items
  const rootItemsMessage =
    typeof itemsError?.message === 'string' ? itemsError.message : undefined

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <StepHeader title="Items" sub="Quantities, costs and unit-level adjustments" />

      <div className="border-b border-[var(--line-warm)] bg-[var(--surface-page)] px-6 py-2.5">
        {!disabled && (
          <>
            <ProductSearchPicker
              onPick={(item) => append(item, { shouldFocus: false })}
              onScan={handleScan}
              disabled={disabled}
              supplier={supplier || null}
              vendor={supplier ? null : vendor || null}
              addedVariantIds={new Set(fields.map((f) => Number(f.variant_id)))}
            />
            {scanError && (
              <div
                role="alert"
                className="mt-1.5 text-xs text-[var(--status-rejected-fg)]"
              >
                {scanError}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {fields.length === 0 ? (
          <div className="px-6 py-10 text-center text-base text-[var(--mute)]">
            No items yet. Search above to add products.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-header)]">
              <tr>
                <th className="tbl-th w-1 p-0"></th>
                <th className="tbl-th text-left">Item</th>
                <th className="tbl-th w-[180px] text-left">
                  Item note
                </th>
                <th className="tbl-th w-[120px] text-right">
                  Stock / Incoming
                </th>
                <th className="tbl-th w-[60px] text-right">
                  Rec
                </th>
                <th className="tbl-th w-[92px] text-right">
                  Ordered
                </th>
                {showReceived && (
                  <th className="tbl-th w-[124px] text-right">
                    Received
                  </th>
                )}
                <th className="tbl-th w-[96px] text-right">
                  Unit cost
                </th>
                <th className="tbl-th w-[90px] text-right">
                  Line
                </th>
                <th className="tbl-th w-9"></th>
                <th className="tbl-th w-9"></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((row, index) => {
                const itemErrors = formState.errors.items?.[index]
                return (
                  <ItemRow
                    key={row.id}
                    index={index}
                    row={row as unknown as POLineItemFormValues}
                    disabled={disabled}
                    showReceived={showReceived}
                    register={register}
                    onRemove={() => remove(index)}
                    onShowDetails={() => setDetailsIndex(index)}
                    onBackorderItem={onBackorderItem}
                    errors={
                      itemErrors as
                        | {
                            cost_base?: { message?: string }
                            qty_ordered?: { message?: string }
                          }
                        | undefined
                    }
                  />
                )
              })}
            </tbody>
          </table>
        )}
        {rootItemsMessage && (
          <div className="px-6 py-3 text-sm text-[var(--status-rejected-fg)]">
            {rootItemsMessage}
          </div>
        )}
      </div>

      {/* Bottom cost summary toolbar — V1-style at the bottom of V2 (per chat1.md:116) */}
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--line-warm)] bg-[var(--surface-page)] px-6 py-3 text-sm">
        <span className="text-[var(--mute)]">
          {totals.itemCount} item{totals.itemCount === 1 ? '' : 's'} ·{' '}
          {totals.units} units
        </span>
        {budget && (
          <>
            <span className="text-[var(--mute)]">·</span>
            <span className="text-[var(--mute)]">Budget</span>
            <BudgetMeter
              spent={projectedSpend(budget.spent_dollars, totals.total)}
              budget={budget.budget_dollars}
              className="w-28"
            />
            <span className="text-[var(--mute)]">
              {fmtCurrency0(projectedSpend(budget.spent_dollars, totals.total))} incl. this PO /{' '}
              {fmtCurrency0(budget.budget_dollars)}
            </span>
          </>
        )}
        <div className="flex-1" />
        <span className="font-semibold">{fmtCurrency(totals.subtotal)} subtotal</span>
        {totals.freightPct > 0 && (
          <span className="text-[var(--mute)]">
            + {fmtCurrency(totals.freight)} freight (
            {(totals.freightPct * 100).toFixed(0)}%)
          </span>
        )}
        <span className="font-semibold">= {fmtCurrency(totals.total)}</span>
      </div>

      <ItemDetailsModal
        open={detailsIndex !== null}
        onOpenChange={(o) => { if (!o) setDetailsIndex(null) }}
        index={detailsIndex}
        onAddItem={(item) => append(item)}
      />
    </div>
  )
}

// Line stripe = the dashboard's signal bar for the same kind, via the same
// FROZEN --signal-* bar tokens, so a line reads exactly as it did on the
// dashboard. Every add path stores `signal` as a dashboard SignalKind
// (signalKind()); 'unknown' / missing gets no stripe.
const SIGNAL_STRIPE: Record<string, string> = {
  order: 'bg-[var(--signal-order)]',
  stop: 'bg-[var(--signal-stop)]',
  high_returns: 'bg-[var(--signal-high-returns-bar)]',
  liquidate: 'bg-[var(--signal-liquidate-bar)]',
  ok: 'bg-[var(--signal-ok)]',
  no_action: 'bg-[var(--signal-no-action-bar)]',
}

function SignalStripe({ signal }: { signal?: string }) {
  return (
    <div
      data-testid="line-signal-stripe"
      data-signal={signal ?? ''}
      className={cn('h-9 w-[3px]', signal && SIGNAL_STRIPE[signal])}
    />
  )
}

function ItemRow({
  index,
  row,
  disabled,
  showReceived,
  register,
  onRemove,
  onShowDetails,
  onBackorderItem,
  errors,
}: {
  index: number
  row: POLineItemFormValues
  disabled: boolean
  showReceived: boolean
  register: UseFormReturn<POFormValues>['register']
  onRemove: () => void
  onShowDetails: () => void
  onBackorderItem?: (variantId: number) => void
  errors: {
    cost_base?: { message?: string }
    qty_ordered?: { message?: string }
  } | undefined
}) {
  const { control } = useFormContext<POFormValues>()
  const cost = useWatch({ control, name: `items.${index}.cost_base` })
  const qty = useWatch({ control, name: `items.${index}.qty_ordered` })
  const line = (Number(cost) || 0) * (Number(qty) || 0)
  const signal = row.signal
  // On a placed/archived PO (showReceived), a line with received units can't be
  // removed via Save — the PATCH reconcile would delete its qty_received + sync
  // history (the #3548 data-loss path; the backend also 409s this). Correct
  // receipts on the receiving page, or use Backorder for a placed line.
  const lockRemove = showReceived && Number(row.qty_received ?? 0) > 0

  return (
    <tr className="border-b border-[var(--line-warm)]">
      <td className="p-0 align-middle">
        <SignalStripe signal={signal} />
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="font-medium">{row.product_title}</div>
        <div className="text-xs text-[var(--mute)]">
          {row.variant_title || '—'} · {row.barcode || '—'} · SKU {row.sku || '—'}
          {row.vendor ? ` · ${row.vendor}` : ''}
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        {/* Per-line item note. Round-trips through the existing POLineItemCreate
            `notes` field — backend already persists it; this just surfaces an
            editor in the Items step. */}
        <Input
          type="text"
          disabled={disabled}
          className="h-8 text-xs"
          placeholder="Note…"
          aria-label={`Note for ${row.product_title}`}
          {...register(`items.${index}.notes`)}
        />
      </td>
      <td className="px-3 py-2 text-right align-middle tabular-nums text-[var(--mute)]">
        {row.on_hand ?? '—'} / {row.incoming ?? 0}
      </td>
      <td className="px-3 py-2 text-right align-middle tabular-nums text-[var(--mute)]">
        {row.rec_qty ?? '—'}
      </td>
      <td className="px-3 py-2 text-right align-middle">
        <Input
          type="number"
          min="1"
          step="1"
          disabled={disabled}
          className="h-8 w-[70px] text-right tabular-nums"
          aria-label={`Quantity for ${row.product_title}`}
          {...register(`items.${index}.qty_ordered`, { valueAsNumber: true })}
        />
        {errors?.qty_ordered && (
          <div className="mt-1 text-right text-xs text-[var(--status-rejected-fg)]">
            {errors.qty_ordered.message}
          </div>
        )}
      </td>
      {showReceived && (
        <td
          className={cn(
            'px-3 py-2 text-right align-middle tabular-nums',
            // Flag an over-receipt (got more than ordered) in amber (attention) so it
            // stands out; otherwise primary text like the surrounding cells.
            Number(row.qty_received ?? 0) > Number(row.qty_ordered ?? 0)
              ? 'font-semibold text-[var(--status-awaiting-fg)]'
              : 'text-[var(--text-primary)]'
          )}
        >
          {/* "qty / Sep 16, 2026" — qty first at full cell size; date follows,
              smaller. Both black. Date + "/" only show once received. */}
          <div className="flex items-baseline justify-end gap-1">
            <span>{row.qty_received ?? 0}</span>
            {Number(row.qty_received ?? 0) > 0 && fmtDate(row.last_received_at) && (
              <span className="text-[10px] font-normal whitespace-nowrap">
                / {fmtDate(row.last_received_at)}
              </span>
            )}
          </div>
        </td>
      )}
      <td className="px-3 py-2 text-right align-middle">
        <Input
          type="number"
          step="0.01"
          min="0"
          disabled={disabled}
          className="h-8 w-[86px] text-right tabular-nums"
          aria-label={`Cost for ${row.product_title}`}
          {...register(`items.${index}.cost_base`, { valueAsNumber: true })}
        />
        {errors?.cost_base && (
          <div className="mt-1 text-right text-xs text-[var(--status-rejected-fg)]">
            {errors.cost_base.message}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right align-middle font-semibold tabular-nums">
        {fmtCurrency(line)}
      </td>
      <td className="px-1 py-2 text-right align-middle">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onShowDetails}
          aria-label={`Details for ${row.product_title}`}
        >
          <Info className="h-4 w-4" />
        </Button>
      </td>
      <td className="px-3 py-2 text-right align-middle">
        <div className="flex items-center justify-end gap-0.5">
          {onBackorderItem && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => onBackorderItem(row.variant_id)}
              aria-label={`Move ${row.product_title} to backorder`}
              title="Move to backorder / OOS list"
            >
              <PackageX className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || lockRemove}
            onClick={onRemove}
            aria-label={`Remove ${row.product_title}`}
            title={
              lockRemove
                ? "Can't remove a line with received units — adjust receipts on the receiving page, or use Backorder"
                : undefined
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────
// Step 3: Review
// ────────────────────────────────────────────────────────────────────
function StepReview({
  disabled,
  poStatus,
  approvalNotesThread,
  onApprove,
  onReject,
  approveBusy = false,
  rejectBusy = false,
  onPostNote,
  postNoteBusy = false,
}: {
  disabled: boolean
  poStatus?: string
  approvalNotesThread?: string | null
  onApprove?: () => void | Promise<void>
  onReject?: (reason: string) => void | Promise<void>
  approveBusy?: boolean
  rejectBusy?: boolean
  onPostNote?: (text: string) => void | Promise<void>
  postNoteBusy?: boolean
}) {
  const { control } = useFormContext<POFormValues>()
  const items = useWatch({ control, name: 'items' }) ?? []
  // Current add-a-note text — drives the Post note button's blank-disable.
  const noteText = useWatch({ control, name: 'approval_notes' }) ?? ''
  const expected = useWatch({ control, name: 'expected_delivery' })
  const totals = usePOTotals()
  const { data: budget } = usePOBudget()
  const isAwaitingApproval = poStatus === 'AWAITING_APPROVAL'
  // Mirror the Items step: surface a Received column once the PO is ordered.
  const showReceived =
    poStatus === 'ORDERED' ||
    poStatus === 'PARTIAL_ORDERED' ||
    poStatus === 'ARCHIVED'
  const [rejectMode, setRejectMode] = React.useState(false)
  const [rejectReason, setRejectReason] = React.useState('')
  const canSubmitReject = rejectReason.trim().length >= 3

  // Session 7.5 — vendor mix recap. Group items by their per-item vendor so
  // the reviewer can see at a glance which brands the PO spans (e.g. "Items
  // from: B (3), C (2), D (1)"). Order by count desc, then alpha.
  const vendorCounts = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const it of items) {
      const v = (it.vendor ?? '').trim() || '(unspecified)'
      map.set(v, (map.get(v) ?? 0) + 1)
    }
    return Array.from(map.entries()).sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    )
  }, [items])

  // Checks list — derived from form + vendor data
  const allHaveBarcodes = items.every((it) => !!it.barcode)
  const highReturnItems = items.filter((it) => (it.return_rate ?? 0) > 0.15)

  const checks: { ok: boolean; warn?: boolean; label: string }[] = [
    {
      ok: totals.movMet,
      label: totals.movMet
        ? `MOV ${fmtCurrency0(totals.mov)} met`
        : `Below MOV (${fmtCurrency0(totals.mov)})`,
    },
    // Expected delivery is optional at create time; only surface it as an
    // info row when it's set, never as a failing check.
    ...(expected
      ? [{ ok: true, label: `Expected delivery set (${expected})` }]
      : []),
    {
      ok: allHaveBarcodes,
      label: allHaveBarcodes
        ? 'All items have barcodes'
        : 'Some items missing barcodes',
    },
    ...(highReturnItems.length > 0
      ? [
          {
            ok: false,
            warn: true,
            label: `${highReturnItems.length} item${
              highReturnItems.length === 1 ? '' : 's'
            } with return rate > 15% — review`,
          },
        ]
      : []),
  ]

  const projectedSpent = projectedSpend(budget?.spent_dollars ?? 0, totals.total)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <StepHeader
        title="Review & submit"
        sub="Double-check totals, then send for approval or order directly"
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Left: items recap */}
        <div className="flex flex-1 flex-col overflow-hidden border-r border-[var(--line-warm)]">
          <div className="flex items-center gap-2 border-b border-[var(--line-warm)] px-4 py-2.5 text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
            <span>Line items</span>
            <span>
              · {totals.itemCount} SKUs · {totals.units} units
            </span>
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface-header)]">
                <tr>
                  <th className="tbl-th w-1 p-0"></th>
                  <th className="tbl-th text-left">Item</th>
                  <th className="tbl-th w-[80px] text-right">
                    Ordered
                  </th>
                  {showReceived && (
                    <th className="tbl-th w-[116px] text-right">
                      Received
                    </th>
                  )}
                  <th className="tbl-th w-[80px] text-right">
                    Unit
                  </th>
                  <th className="tbl-th w-[96px] text-right">
                    Line
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-[var(--line-warm)]">
                    <td className="p-0 align-middle">
                      <SignalStripe signal={it.signal} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.product_title}</div>
                      <div className="text-xs text-[var(--mute)]">
                        {it.variant_title || '—'} · {it.barcode || '—'} · SKU {it.sku || '—'}
                        {it.vendor ? ` · ${it.vendor}` : ''}
                      </div>
                      {/* Per-line item note, read-only here — editing stays
                          on the Items step. */}
                      {(it.notes ?? '').trim() && (
                        <div className="text-xs italic text-[var(--mute)]">
                          {it.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {it.qty_ordered}
                    </td>
                    {showReceived && (
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          Number(it.qty_received ?? 0) > Number(it.qty_ordered ?? 0)
                            ? 'font-semibold text-[var(--status-awaiting-fg)]'
                            : 'text-[var(--text-primary)]'
                        )}
                      >
                        <div className="flex items-baseline justify-end gap-1">
                          <span>{it.qty_received ?? 0}</span>
                          {Number(it.qty_received ?? 0) > 0 &&
                            fmtDate(it.last_received_at) && (
                              <span className="text-[10px] font-normal whitespace-nowrap">
                                / {fmtDate(it.last_received_at)}
                              </span>
                            )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtCurrency(it.cost_base)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {fmtCurrency(it.cost_base * it.qty_ordered)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: totals + budget + checks + approval notes */}
        <div className="flex w-[340px] flex-col gap-4 overflow-y-auto p-5">
          <div>
            <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
              Totals
            </div>
            <div className="mt-2 flex flex-col gap-1.5">
              <TotalsRow label="Subtotal" value={fmtCurrency(totals.subtotal)} />
              {totals.freightPct > 0 && (
                <TotalsRow
                  label={`Freight (${(totals.freightPct * 100).toFixed(0)}%)`}
                  value={fmtCurrency(totals.freight)}
                />
              )}
              {totals.dutyPct > 0 && (
                <TotalsRow
                  label={`Duty (${(totals.dutyPct * 100).toFixed(0)}%)`}
                  value={fmtCurrency(totals.duty)}
                />
              )}
              <div className="mt-1 flex items-baseline justify-between border-t border-[var(--line-warm)] pt-1.5">
                <span className="font-semibold">Total</span>
                <span className="text-lg font-semibold tabular-nums">
                  {fmtCurrency(totals.total)}
                </span>
              </div>
            </div>
          </div>

          {vendorCounts.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
                Items from
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-1 text-xs">
                {vendorCounts.map(([name, count]) => (
                  <span key={name} className="tabular-nums">
                    <span className="font-medium">{name}</span>
                    <span className="text-[var(--mute)]"> ({count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {budget && (
            <div>
              <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
                Budget impact
              </div>
              <BudgetMeter
                spent={projectedSpent}
                budget={budget.budget_dollars}
                className="mt-1.5"
              />
              <div className="mt-1 flex justify-between text-xs text-[var(--mute)]">
                <span>{fmtCurrency0(projectedSpent)} incl. this PO</span>
                <span>of {fmtCurrency0(budget.budget_dollars)} this month</span>
              </div>
            </div>
          )}

          <div className="border-t border-[var(--line-warm)] pt-3">
            <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
              Checks
            </div>
            <div className="mt-2 flex flex-col gap-1.5 text-[11px]">
              {checks.map((c, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex items-center gap-2',
                    c.warn && 'text-[var(--status-awaiting-fg)]',
                    !c.ok && !c.warn && 'text-[var(--status-rejected-fg)]'
                  )}
                >
                  <span className="font-bold">
                    {c.ok ? '✓' : c.warn ? '!' : '✗'}
                  </span>
                  <span>{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stored approval-notes thread — read-only history. The server
              appends a line per submit/resubmit/approve note; whitespace-
              pre-line keeps one thread line per row. */}
          {(approvalNotesThread ?? '').trim() && (
            <div className="border-t border-[var(--line-warm)] pt-3">
              <div className="text-xs uppercase tracking-[0.06em] text-[var(--text-primary)]">
                Approval notes
              </div>
              <div className="mt-1.5 whitespace-pre-line text-xs leading-[1.6] text-[var(--text-primary)]">
                {approvalNotesThread}
              </div>
            </div>
          )}

          <div className="border-t border-[var(--line-warm)] pt-3">
            {/* Fresh, initially-empty input — sent as the `{ notes }` body on
                submit / resubmit / approve, where the server appends it to
                the thread above. Never prefilled with the stored thread: an
                untouched box must not create a history entry. */}
            <FormField
              control={control}
              name="approval_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add a note (optional)</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      // Note controls stay usable regardless of the wizard's
                      // disabled state when Post note is available — a user
                      // must be able to comment on an ORDERED / ARCHIVED PO.
                      // Only an in-flight post locks the box.
                      disabled={onPostNote ? postNoteBusy : disabled}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      rows={3}
                      placeholder="Reason for ordering, vendor context…"
                      className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {/* Post the note immediately as a conversation entry — additional
                to (not replacing) the note riding along on submit/resubmit/
                approve. Only rendered for saved POs (onPostNote supplied). */}
            {onPostNote && (
              <div className="mt-2 flex items-center justify-end gap-2">
                <span className="text-xs text-[var(--mute)]">
                  Notifies the other party by email
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={postNoteBusy || !noteText.trim()}
                  onClick={() => onPostNote(noteText.trim())}
                  data-action="post-note"
                >
                  {postNoteBusy ? 'Posting…' : 'Post note'}
                </Button>
              </div>
            )}
          </div>

          {/* Inline Approve / Reject — shown only for AWAITING_APPROVAL POs.
              The approver acts here without being kicked into a second
              modal. Reject reveals a required reason textarea (min 3 chars)
              and disables Confirm reject until the user types one. */}
          {isAwaitingApproval && (onApprove || onReject) && (
            <div className="border-t border-[var(--line-warm)] pt-3">
              <div className="text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
                Approval decision
              </div>
              {!rejectMode ? (
                <div className="mt-2 flex gap-2">
                  {onApprove && (
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      disabled={disabled || approveBusy || rejectBusy}
                      onClick={() => onApprove?.()}
                      data-action="inline-approve"
                    >
                      {approveBusy ? 'Approving…' : 'Approve'}
                    </Button>
                  )}
                  {onReject && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs text-destructive hover:text-destructive"
                      disabled={disabled || approveBusy || rejectBusy}
                      onClick={() => setRejectMode(true)}
                      data-action="inline-reject"
                    >
                      Reject
                    </Button>
                  )}
                </div>
              ) : (
                <div className="mt-2 flex flex-col gap-2">
                  <FormLabel className="text-xs text-[var(--mute)]">
                    Reason for rejection
                  </FormLabel>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    disabled={disabled || rejectBusy}
                    rows={3}
                    placeholder="Why is this PO being rejected? (min 3 characters)"
                    autoFocus
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={() => {
                        setRejectMode(false)
                        setRejectReason('')
                      }}
                      disabled={rejectBusy}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      disabled={!canSubmitReject || rejectBusy}
                      onClick={() => onReject?.(rejectReason.trim())}
                      data-action="inline-reject-confirm"
                    >
                      {rejectBusy ? 'Rejecting…' : 'Confirm reject'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TotalsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-xs text-[var(--mute)]">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Wizard shell
// ────────────────────────────────────────────────────────────────────
export function POWizard({
  step,
  disabled = false,
  onStepClick,
  poStatus,
  approvalNotesThread,
  onApprove,
  onReject,
  approveBusy = false,
  rejectBusy = false,
  onPostNote,
  postNoteBusy = false,
  onBackorderItem,
}: POWizardProps) {
  const { control } = useFormContext<POFormValues>()
  const supplier = useWatch({ control, name: 'supplier' }) ?? ''
  const vendor = useWatch({ control, name: 'vendor' }) ?? ''
  const location = useWatch({ control, name: 'location_to_receive' })

  return (
    <div className="flex h-full flex-1 overflow-hidden">
      <StepperRail
        step={step}
        filledSupplier={supplier}
        filledVendor={vendor}
        filledLocation={location}
        onStepClick={onStepClick}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        {step === 1 && <StepSetup disabled={disabled} />}
        {step === 2 && (
          <StepItems
            disabled={disabled}
            poStatus={poStatus}
            onBackorderItem={onBackorderItem}
          />
        )}
        {step === 3 && (
          <StepReview
            disabled={disabled}
            poStatus={poStatus}
            approvalNotesThread={approvalNotesThread}
            onApprove={onApprove}
            onReject={onReject}
            approveBusy={approveBusy}
            rejectBusy={rejectBusy}
            onPostNote={onPostNote}
            postNoteBusy={postNoteBusy}
          />
        )}
      </div>
    </div>
  )
}

// Re-exported for the parent modals (used in their footers)
export const wizardStepsCount = 3 as const
